/**
 * A labelled form field with its error message.
 *
 * Bundled so the label/input/error wiring cannot be got wrong one field at a
 * time: the ids are generated here, and the error is both announced and
 * visible.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.6, PRD §9.7
 */
import { useId } from 'react';

import { cn } from '../../lib/cn';

export interface FieldProps {
  label: string;
  /** Shown when the field is invalid. Announced, not only coloured. */
  error?: string | undefined;
  /** Guidance shown under the label. */
  hint?: string | undefined;
  required?: boolean;
  className?: string;
  /** Receives the attributes to wire onto the control. */
  children: (props: {
    id: string;
    errorId: string;
    invalid: boolean;
    /* Passed through rather than left to the caller: the asterisk is
       decorative, so `required` on the control is the only thing assistive
       technology reports — and a caller who forgets it produces a field that
       looks required and does not announce as one. */
    required: boolean;
    'aria-describedby': string | undefined;
  }) => React.ReactNode;
}

export function Field({
  label,
  error,
  hint,
  required = false,
  className,
  children,
}: FieldProps): React.JSX.Element {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const invalid = error !== undefined;

  /* Both the hint and the error are referenced, in that order, so a screen
     reader reads the guidance before the failure. */
  const describedBy = [hint === undefined ? null : hintId, invalid ? errorId : null]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {/* The label's accessible name is its text content, and `aria-hidden`
          on a descendant does NOT remove that descendant from the name
          computation — so an asterisk inside the label makes the field
          announce as "Name star". The marker sits OUTSIDE the label element
          instead; `required` on the control is what assistive technology
          reports, which is the correct signal anyway. */}
      <span className="flex items-baseline gap-1">
        <label htmlFor={id} className="text-navy-text text-sm font-semibold">
          {label}
        </label>
        {required ? (
          <span aria-hidden="true" className="text-sem-bad text-sm">
            *
          </span>
        ) : null}
      </span>

      {hint === undefined ? null : (
        <p id={hintId} className="text-gray-400 text-xs">
          {hint}
        </p>
      )}

      {children({
        id,
        errorId,
        invalid,
        required,
        'aria-describedby': describedBy === '' ? undefined : describedBy,
      })}

      {/* `role="alert"` so a validation failure is announced when it appears. */}
      {invalid ? (
        <p id={errorId} role="alert" className="text-sem-bad text-xs">
          {error}
        </p>
      ) : null}
    </div>
  );
}
